import { BackendSource, isSourceAccessible } from ".";
import { NewsFeedItem, SOURCE_TIME_FIELDS } from "./models";
import useFirehoseStore from "@/stores/FirehoseStore";
import { firehosePortfolioMatcher } from "@/widget-components/firehose-widgets/firehose-all/utils";
import { playBeep, playSpoon } from "@/utils/sound-utils";
import type { Portfolio } from "@/types/Portfolio";

const STALENESS_THRESHOLD_MS = 5 * 60 * 1000;

const STOCKS_NEWS_SOURCES: BackendSource[] = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "stocks-four",
  "stocks-four-filings",
];

type PortfoliosWithSoundEnabled = Partial<Record<string, Portfolio[]>>;

let authContext: { roles: string[]; isInternalUser: boolean } = {
  roles: [],
  isInternalUser: false,
};

let soundContext: {
  portfoliosWithSoundEnabled: PortfoliosWithSoundEnabled | null;
  notificationsSoundTruthSocial: boolean;
} = {
  portfoliosWithSoundEnabled: null,
  notificationsSoundTruthSocial: false,
};

export function setFirehoseAuthContext(ctx: {
  roles: string[];
  isInternalUser: boolean;
}) {
  authContext = ctx;
}

export function setFirehoseSoundContext(ctx: {
  portfoliosWithSoundEnabled: PortfoliosWithSoundEnabled | null;
  notificationsSoundTruthSocial: boolean;
}) {
  soundContext = ctx;
}

export function normalizeSourceTime(story: NewsFeedItem): void {
  const sourceTimeField = SOURCE_TIME_FIELDS[story.backend_source];
  if (sourceTimeField && (story as any)[sourceTimeField]) {
    story.source_published_time = (story as any)[sourceTimeField];
  }
}

export interface IngestOptions {
  /** Skip the sound-notification path — used by the polling fallback path. */
  skipSound?: boolean;
}

/**
 * Single ingestion path used by both the live SSE handler and the polling
 * fallback. Idempotent: reads current store state and skips already-known ids
 * so repeated polls don't duplicate stocks-news entries (addTypeStory itself
 * does not dedupe).
 */
export function ingestFirehoseStory(
  newStory: NewsFeedItem,
  opts: IngestOptions = {}
) {
  const store = useFirehoseStore.getState();
  if (store.allStories.some((s) => s.id === newStory.id)) {
    return;
  }

  if (
    !isSourceAccessible(
      newStory.backend_source,
      authContext.roles,
      authContext.isInternalUser
    )
  ) {
    return;
  }

  newStory.isNew = true;
  newStory.received_at = Date.now();
  normalizeSourceTime(newStory);

  store.addStory(newStory);

  const isStocksNews = STOCKS_NEWS_SOURCES.includes(newStory.backend_source);
  if (isStocksNews) {
    store.addTypeStory(newStory, ["stocks-news"]);
  }

  if (opts.skipSound) return;

  const isStale =
    newStory.source_published_time != null &&
    Date.now() - new Date(newStory.source_published_time).getTime() >
      STALENESS_THRESHOLD_MS;

  if (isStale) return;

  const portfoliosForSource =
    soundContext.portfoliosWithSoundEnabled?.[newStory.backend_source] ?? [];
  const portfoliosForAll =
    soundContext.portfoliosWithSoundEnabled?.allStories ?? [];
  let portfoliosToMatch = [...portfoliosForSource, ...portfoliosForAll];

  if (isStocksNews) {
    const portfoliosForStocksNews =
      soundContext.portfoliosWithSoundEnabled?.["stocks-news"] ?? [];
    portfoliosToMatch = [...portfoliosToMatch, ...portfoliosForStocksNews];
  }

  const matchedPortfolio = firehosePortfolioMatcher(
    newStory,
    portfoliosToMatch
  );

  if (matchedPortfolio) {
    playSpoon();
  } else if (
    newStory.backend_source === "truth-social" &&
    soundContext.notificationsSoundTruthSocial
  ) {
    playBeep();
  }
}
